import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Scale, ChevronLeft } from "lucide-react";

export default function Terms() {
  const updated = "9 September 2026";
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      <SEO title="Terms of Service · Nexus" description="The rules of the Nexus marketplace." path="/terms" />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-8"><ChevronLeft className="h-3 w-3" /> Back</Link>
        <div className="flex items-center gap-3 mb-4">
          <Scale className="h-6 w-6 text-nexus-purple" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Terms of Service</div>
        </div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">The house rules.</h1>
        <p className="text-zinc-500 mt-2 text-sm">Last updated {updated}</p>

        <div className="prose prose-invert prose-zinc max-w-none mt-10 space-y-6 text-zinc-300 text-sm leading-relaxed">
          <section>
            <h2 className="font-display font-bold text-xl text-white mt-0">1. Acceptance</h2>
            <p>These Terms are a contract between you and CollabNexus Ltd (Company No. 17438481, registered in England &amp; Wales, registered office 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ), trading as Nexus. By creating a Nexus account you agree to these Terms. If you don&apos;t, don&apos;t use the platform. You must be at least 18 years old.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">2. Who can use Nexus</h2>
            <p>Nexus is a two-sided marketplace. <strong>Brands</strong> post campaigns and hire creators. <strong>Creators</strong> apply, deliver content, and receive payment. You may hold only one account per person or business.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">3. Payments &amp; escrow</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>All payments go through Stripe. Nexus holds funds in escrow until the brand approves the delivery.</li>
              <li>The platform fee is charged to the brand on top of the agreed price: 20% (Free), 15% (Starter), 10% (Pro) or 10% locked-for-life (Founding). Creators receive 100% of the agreed price — no creator commission.</li>
              <li>Approved deliveries are released to creator payout balance within 24 hours.</li>
              <li>Subscription plans are billed monthly (or annually for Founding) via Stripe and auto-renew unless cancelled.</li>
              <li>Refunds are handled case-by-case; disputes may be routed to Nexus review.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">4. Content ownership</h2>
            <p>Creators retain copyright of their content but grant the hiring brand a perpetual worldwide licence to use the delivered content for the campaign purposes agreed in the brief. Brands may not sub-license or resell creator content.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">5. Prohibited behaviour</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>No fake followers, engagement, or metrics.</li>
              <li>No content that&apos;s illegal, hateful, sexually explicit, or promotes self-harm.</li>
              <li>No off-platform payment attempts to dodge platform fees.</li>
              <li>No harassment or spam.</li>
              <li>No scraping or reverse-engineering the platform.</li>
            </ul>
            <p>Breaches may result in immediate suspension and forfeiture of pending payouts.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">6. Social platform integrations</h2>
            <p>When you connect Instagram, TikTok, or YouTube, you authorise Nexus to read your public profile and follower/engagement metrics via each platform&apos;s official OAuth flow. You may disconnect at any time from Settings; we will delete stored tokens immediately.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">7. Termination</h2>
            <p>Either party may terminate at any time. If we suspend your account for material breach, pending funds may be held until resolution. Full data deletion is available via our <Link to="/data-deletion" className="text-nexus-blue hover:text-white">Data Deletion page</Link>.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">8. Liability</h2>
            <p>Nexus is a marketplace intermediary. We are not liable for content created, agreements between brands and creators, or campaign outcomes. Our aggregate liability is capped at the fees you paid us in the last 12 months.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">9. Governing law</h2>
            <p>These Terms are governed by the laws of England and Wales. Any dispute is subject to the exclusive jurisdiction of the courts of England and Wales.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">10. Contact</h2>
            <p>Questions? <a href="mailto:info@collabnexus.co.uk" className="text-nexus-blue hover:text-white">info@collabnexus.co.uk</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
